import { Box, Typography, Skeleton, SvgIcon } from "@mui/material";
import { DataGridPro, GridRenderCellParams, GridSortModel } from "@mui/x-data-grid-pro";
import { useGetContentModelFieldsQuery, useGetContentModelItemsQuery, useGetContentModelsQuery } from "../../../../../shell/services/instance";
import { ContentModel, ModelType } from "../../../../../shell/services/types";
import { formatDistanceToNowLocalized } from "shell/i18n/dates";
import { useTranslation } from "react-i18next";
import { isValid } from "date-fns";
import { useMemo, useState, useReducer } from "react";
import AutoSizer, { Size } from "react-virtualized-auto-sizer";
import { useHistory } from "react-router";
import { NoResults } from "./NoResults";
import { modelIconMap, modelNameMap } from "../utils";
import { Filters } from "./Filters";
import { AllModelsEmptyState } from "./AllModelsEmptyState";
import { DateFilterValue, getDateFilterFn } from "../../../../../shell/components/Filters/DateFilter";
const FieldsCell = ({
  ZUID
}: any) => {
  const {
    data,
    isLoading
  } = useGetContentModelFieldsQuery({
    modelZUID: ZUID
  });
  if (isLoading) {
    return <Box display="flex" height="100%" alignItems="center">
        <Skeleton variant="rectangular" width="100%" />
      </Box>;
  }
  return <Box display="flex" flexDirection="column" height="100%" justifyContent="center" data-sentry-element="Box" data-sentry-component="FieldsCell" data-sentry-source-file="ModelsTable.tsx">
      <Typography variant="body2" data-sentry-element="Typography" data-sentry-source-file="ModelsTable.tsx">{data?.length}</Typography>
    </Box>;
};
const ContentItemsCell = ({
  ZUID
}: any) => {
  const {
    data,
    isLoading
  } = useGetContentModelItemsQuery({
    modelZUID: ZUID
  });
  if (isLoading) {
    return <Box display="flex" height="100%" alignItems="center">
        <Skeleton variant="rectangular" width="100%" />
      </Box>;
  }
  return <Box display="flex" flexDirection="column" height="100%" justifyContent="center" data-sentry-element="Box" data-sentry-component="ContentItemsCell" data-sentry-source-file="ModelsTable.tsx">
      <Typography variant="body2" data-sentry-element="Typography" data-sentry-source-file="ModelsTable.tsx">{data?.length}</Typography>
    </Box>;
};
export type ModelFilter = {
  modelType: ModelType | "";
  user: string;
  lastUpdated: DateFilterValue;
};
interface Props {
  search?: string;
  onEmptySearch?: () => void;
}
export const ModelsTable = ({
  search,
  onEmptySearch
}: Props) => {
  const {
    t
  } = useTranslation();
  const history = useHistory();
  // Used to initialize the table sorted by name
  const [sortModel, setSortModel] = useState<GridSortModel>([{
    field: "label",
    sort: "asc"
  }]);
  const {
    data: models,
    isFetching
  } = useGetContentModelsQuery();
  const [activeFilters, setActiveFilters] = useReducer((currentFilters: ModelFilter, newFilter: Partial<ModelFilter>) => {
    return {
      ...currentFilters,
      ...newFilter
    };
  }, {
    modelType: "",
    user: "",
    lastUpdated: {
      type: "",
      value: ""
    }
  });
  const filteredModels = useMemo(() => {
    let localModels = models;
    if (Object.values(activeFilters).some(filter => filter !== "")) {
      localModels = models?.filter((model: ContentModel) => {
        let dateFilterFn = (date: string) => true;
        const {
          type,
          value
        } = activeFilters.lastUpdated;
        if (type && value) {
          dateFilterFn = getDateFilterFn({
            type,
            value
          });
        }
        return (activeFilters.modelType === "" ? true : model.type === activeFilters.modelType) && (activeFilters.user === "" ? true : model.createdByUserZUID === activeFilters.user) && dateFilterFn(model.updatedAt);
      });
    }
    if (!search) {
      return localModels;
    }
    return localModels?.filter((model: ContentModel) => {
      return model.label?.toLowerCase()?.includes(search.toLowerCase()) || model.name?.toLowerCase()?.includes(search.toLowerCase()) || t(modelNameMap[model.type as keyof typeof modelNameMap])?.toLowerCase()?.includes(search.toLowerCase()) || model.ZUID?.toLowerCase() === search.toLowerCase();
    });
  }, [search, models, activeFilters, t]);
  const handleRowClick = (row: ContentModel) => {
    history.push(`/schema/${row.ZUID}`);
  };
  const columns = [{
    field: "label",
    headerName: t("schema.modelsTableColumnName"),
    flex: 1,
    renderCell: ({
      row
    }: GridRenderCellParams) => <Box display="flex" gap={2} width="100%" height="100%" alignItems="center">
          <SvgIcon fontSize="small" color="action" component={modelIconMap[row.type as keyof typeof modelIconMap]} />
          <Typography variant="body2" noWrap>
            {row.label}
          </Typography>
        </Box>
  }, {
    field: "type",
    headerName: t("schema.modelsTableColumnType"),
    width: 152,
    valueFormatter: (value: any) => t(modelNameMap[value as keyof typeof modelNameMap])
  }, {
    field: "updatedAt",
    headerName: t("schema.modelsTableColumnUpdated"),
    width: 120,
    valueFormatter: (value: any) => {
      const d = new Date(value);
      return isValid(d) ? formatDistanceToNowLocalized(d, {
        addSuffix: true
      }) : "";
    }
  }, {
    field: "fields",
    headerName: t("schema.modelsTableColumnFields"),
    width: 104,
    headerAlign: "right",
    align: "right",
    sortable: false,
    renderCell: ({
      row
    }: GridRenderCellParams) => <FieldsCell {...row} />
  }, {
    field: "contentItems",
    headerName: t("schema.modelsTableColumnContentItems"),
    width: 126,
    headerAlign: "right",
    align: "right",
    sortable: false,
    renderCell: ({
      row
    }: GridRenderCellParams) => <ContentItemsCell {...row} />
  }];
  if (!models?.length && !isFetching) {
    return <AllModelsEmptyState />;
  }
  return <Box height={filteredModels?.length ? "100%" : "55px"} display="flex" flexDirection="column" data-sentry-element="Box" data-sentry-component="ModelsTable" data-sentry-source-file="ModelsTable.tsx">
      <Filters activeFilters={activeFilters} onChange={setActiveFilters} data-sentry-element="Filters" data-sentry-source-file="ModelsTable.tsx" />
      <AutoSizer data-sentry-element="AutoSizer" data-sentry-source-file="ModelsTable.tsx">
        {({
        width,
        height
      }: Size) => <DataGridPro style={{
        width,
        height: height - 40
      }}
      // @ts-expect-error - missing types for headerAlign and align on DataGridPro
      columns={columns} rows={filteredModels?.map(model => ({
        ...model,
        id: model.ZUID
      })) || []} loading={isFetching} hideFooter rowHeight={52} disableSelectionOnClick disableColumnResize disableColumnFilter disableColumnMenu onRowClick={params => handleRowClick(params.row)} sortModel={sortModel} onSortModelChange={model => {
        if (!model.length) {
          setSortModel([{
            ...sortModel[0],
            sort: "asc"
          }]);
        } else {
          setSortModel(model);
        }
      }} sx={{
        ".MuiDataGrid-row": {
          cursor: "pointer",
          backgroundColor: "background.paper"
        },
        "& .MuiDataGrid-columnHeaders": {
          backgroundColor: "grey.100"
        }
      }} />}
      </AutoSizer>
      {!filteredModels?.length && !isFetching && search && <Box sx={{
      mt: 10
    }}>
          <NoResults type="search" searchTerm={search} onButtonClick={() => onEmptySearch()} />
        </Box>}

      {!filteredModels?.length && !isFetching && !search && <Box sx={{
      mt: 10
    }}>
          <NoResults type="filter" onButtonClick={() => setActiveFilters({
        modelType: "",
        user: "",
        lastUpdated: {
          type: "",
          value: ""
        }
      })} />
        </Box>}
    </Box>;
};